import { keyframes } from '@emotion/react';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

import {
  calculateMintingStats,
  getClaims,
  getExplorerUrl,
  retryMint,
  retryVideoGeneration,
  revokeMintWorker,
} from '@/core/services/event-admin';
import { db } from '@/core/services/firebase';
import type { AdminClaim, ClaimStatus, MintingStats } from '@/core/types';

type StatusFilter = 'all' | 'not-completed' | 'permanently-failed';

const statusColors: Record<ClaimStatus, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  ready: 'default',
  'video-queued': 'default',
  'video-processing': 'info',
  'video-done': 'info',
  'video-failed': 'warning',
  'mint-queued': 'default',
  'mint-processing': 'info',
  'mint-pending': 'warning',
  'mint-failed': 'warning',
  completed: 'success',
  'permanently-failed': 'error',
};

const statusLabels: Record<ClaimStatus, string> = {
  ready: '準備中',
  'video-queued': '動画生成待ち',
  'video-processing': '動画生成中',
  'video-done': '動画完了',
  'video-failed': '動画生成失敗',
  'mint-queued': 'ミント待ち',
  'mint-processing': 'ミント中',
  'mint-pending': 'TX完了待ち',
  'mint-failed': 'ミント失敗',
  completed: '完了',
  'permanently-failed': '要対応',
};

export function MintingPage() {
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const theme = useTheme();
  const adminGradient = theme.custom?.adminGradient || theme.custom?.gradient || theme.palette.primary.main;
  const [claims, setClaims] = useState<AdminClaim[]>([]);
  const [stats, setStats] = useState<MintingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [realtime, setRealtime] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>(() => {
    const saved = localStorage.getItem('admin-minting-filter');
    return (saved as StatusFilter) || 'all';
  });

  // リアルタイム更新
  useEffect(() => {
    if (!raceAlias || !realtime) return;

    setLoading(true);
    setError(null);

    const claimsRef = collection(db, 'claims');
    const q = query(
      claimsRef,
      where('raceAlias', '==', raceAlias),
      where('deletedAt', '==', null),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as AdminClaim
        );
        setClaims(data);
        setStats(calculateMintingStats(data));
        setLoading(false);
      },
      (err) => {
        console.error('Realtime update failed:', err);
        setError('リアルタイム更新に失敗しました');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [raceAlias, realtime]);

  // 手動更新
  const fetchData = useCallback(async () => {
    if (!raceAlias) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getClaims(raceAlias);
      setClaims(data);
      setStats(calculateMintingStats(data));
    } catch (err) {
      console.error('Failed to fetch claims:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [raceAlias]);

  // 初回読み込み
  useEffect(() => {
    if (!realtime) {
      fetchData();
    }
  }, [fetchData, realtime]);

  const handleFilterChange = (newFilter: StatusFilter) => {
    setFilter(newFilter);
    localStorage.setItem('admin-minting-filter', newFilter);
  };

  const handleRetryVideo = async (id: string) => {
    try {
      await retryVideoGeneration(id);
      if (!realtime) fetchData();
    } catch (err) {
      console.error('Failed to retry video:', err);
    }
  };

  const handleRetryMint = async (id: string) => {
    try {
      await retryMint(id);
      if (!realtime) fetchData();
    } catch (err) {
      console.error('Failed to retry mint:', err);
    }
  };

  const handleRevoke = async (walletIndex: number) => {
    try {
      await revokeMintWorker(String(walletIndex));
      if (!realtime) fetchData();
    } catch (err) {
      console.error('Failed to revoke wallet:', err);
    }
  };

  const filteredClaims =
    filter === 'not-completed'
      ? claims.filter((c) => c.status !== 'completed')
      : filter === 'permanently-failed'
        ? claims.filter((c) => c.status === 'permanently-failed')
        : claims;

  const columns: GridColDef<AdminClaim>[] = [
    { field: 'bib', headerName: 'ゼッケン', width: 100 },
    {
      field: 'status',
      headerName: 'ステータス',
      width: 140,
      renderCell: (params: GridRenderCellParams<AdminClaim>) => {
        const status = params.value as ClaimStatus;
        return <Chip label={statusLabels[status] || status} color={statusColors[status] || 'default'} size="small" />;
      },
    },
    {
      field: 'sourceFileName',
      headerName: 'パターン',
      width: 100,
      valueGetter: (value: string | null) => (value ? value.split('.')[0] : ''),
    },
    { field: 'mint_context', headerName: 'コンテキスト', width: 100 },
    { field: 'destination', headerName: '送信先', width: 150 },
    {
      field: 'wallet',
      headerName: 'ウォレット',
      width: 180,
      renderCell: (params: GridRenderCellParams<AdminClaim>) => {
        const { mintWorkerIndex, mintWalletAddress, status } = params.row;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2">
              {mintWorkerIndex ?? '-'}/{mintWalletAddress?.slice(0, 8) ?? '-'}...
            </Typography>
            {status === 'mint-queued' && mintWorkerIndex !== null && (
              <Button size="small" variant="outlined" onClick={() => handleRevoke(mintWorkerIndex)}>
                Revoke
              </Button>
            )}
          </Box>
        );
      },
    },
    {
      field: 'startedAt',
      headerName: '開始日時',
      width: 160,
      valueGetter: (value: { toDate: () => Date } | null) => (value ? value.toDate() : null),
      valueFormatter: (value: Date | null) => (value ? value.toLocaleString('ja-JP') : '-'),
    },
    {
      field: 'completedAt',
      headerName: '完了日時',
      width: 160,
      valueGetter: (value: { toDate: () => Date } | null) => (value ? value.toDate() : null),
      valueFormatter: (value: Date | null) => (value ? value.toLocaleString('ja-JP') : '-'),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 200,
      renderCell: (params: GridRenderCellParams<AdminClaim>) => {
        const { txHash, chain, status, lastError, id } = params.row;

        if (txHash) {
          return (
            <Link href={getExplorerUrl(chain, txHash)} target="_blank" rel="noopener">
              TX確認
            </Link>
          );
        }

        if (status === 'video-failed' || status === 'permanently-failed') {
          return (
            <Box>
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                {lastError?.slice(0, 30)}...
              </Typography>
              <Button size="small" onClick={() => handleRetryVideo(id)}>
                動画リトライ
              </Button>
            </Box>
          );
        }

        if (status === 'mint-failed') {
          return (
            <Box>
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                {lastError?.slice(0, 30)}...
              </Typography>
              <Button size="small" onClick={() => handleRetryMint(id)}>
                ミントリトライ
              </Button>
            </Box>
          );
        }

        if (status === 'mint-pending') {
          return (
            <Typography variant="caption" color="warning.main">
              TX確認待ち
            </Typography>
          );
        }

        return lastError || '-';
      },
    },
    { field: 'id', headerName: 'ID', width: 220 },
  ];

  // 進行中の件数を計算
  const inProgressCount = stats
    ? stats['video-queued'] + stats['video-processing'] + stats['mint-queued'] + stats['mint-processing']
    : 0;

  // 失敗の件数を計算
  const failedCount = stats ? stats['video-failed'] + stats['mint-failed'] + stats['mint-pending'] : 0;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          {error}
        </Alert>
      )}

      {/* 統計カード */}
      {stats && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            animation: `${fadeInUp} 0.6s ease-out`,
          }}
        >
          {/* メイン統計 + コントロール */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            {/* 統計ボックス */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flexGrow: 1 }}>
              <Box
                sx={{
                  minWidth: 100,
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  backgroundColor: 'action.hover',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  合計
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {stats.total}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 100,
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  backgroundColor: 'success.main',
                  color: 'success.contrastText',
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  完了
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {stats.completed}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 100,
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  backgroundColor: 'info.main',
                  color: 'info.contrastText',
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  進行中
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {inProgressCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 100,
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  backgroundColor: 'warning.main',
                  color: 'warning.contrastText',
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  失敗/保留
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {failedCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 100,
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  要対応
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {stats['permanently-failed']}
                </Typography>
              </Box>
            </Box>

            {/* コントロール */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <FormControlLabel
                control={<Switch checked={realtime} onChange={(e) => setRealtime(e.target.checked)} color="success" />}
                label="リアルタイム更新"
                sx={{ mr: 0 }}
              />
              {!realtime && (
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchData}
                  sx={{
                    borderRadius: '16px',
                    border: '2px solid transparent',
                    backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    color: 'text.primary',
                    '&:hover': {
                      backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${adminGradient}`,
                    },
                  }}
                >
                  更新
                </Button>
              )}
            </Box>
          </Box>

          {/* 内訳 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Chip label={`会場: ${stats.on_site}`} variant="outlined" />
            <Chip label={`オンライン: ${stats.online}`} variant="outlined" />
            {Object.entries(stats.patterns).map(([pattern, count]) => (
              <Chip
                key={pattern}
                label={`${pattern}: ${count} (${((count / stats.total) * 100).toFixed(1)}%)`}
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* フィルター */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, animation: `${fadeInUp} 0.6s ease-out 0.2s both` }}>
        <Chip
          label="すべて"
          onClick={() => handleFilterChange('all')}
          color={filter === 'all' ? 'primary' : 'default'}
          clickable
          sx={{
            fontWeight: filter === 'all' ? 600 : 400,
            ...(filter === 'all' && {
              background: adminGradient,
              '& .MuiChip-label': { color: '#fff' },
            }),
          }}
        />
        <Chip
          label="未完了のみ"
          onClick={() => handleFilterChange('not-completed')}
          color={filter === 'not-completed' ? 'primary' : 'default'}
          clickable
          sx={{
            fontWeight: filter === 'not-completed' ? 600 : 400,
            ...(filter === 'not-completed' && {
              background: adminGradient,
              '& .MuiChip-label': { color: '#fff' },
            }),
          }}
        />
        <Chip
          label="要対応のみ"
          onClick={() => handleFilterChange('permanently-failed')}
          color={filter === 'permanently-failed' ? 'error' : 'default'}
          clickable
          sx={{ fontWeight: filter === 'permanently-failed' ? 600 : 400 }}
        />
      </Box>

      {/* データグリッド */}
      <Paper
        sx={{
          height: 600,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: `${fadeInUp} 0.6s ease-out 0.3s both`,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'action.hover',
            borderBottom: '2px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <DataGrid
          rows={filteredClaims}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
}
